// Traduction des produits (titres, descriptions) et des pages en base, SANS toucher à la base ni à l'API :
// des fichiers statiques superposés au français, avec repli sur le français (produit non traduit = français).
// Contrat (docs/multilingue-plan.md §5) — `url` = adresse du produit (`produits_local.url`) :
//   /i18n/produits/<langue>.json          { "<url>": { "titre", "description_courte", "etiquette" } }      (léger, catalogues)
//   /i18n/produits/<langue>/<url>.json    { "description": "…markdown…" }                                   (fiche produit)
//   /i18n/pages/<langue>.json             { "<url>": { "titre", "contenu", "meta_title", "meta_description" } }  (pages en base)
// Le français ne charge JAMAIS rien. Un fichier absent (langue pas encore traduite) = repli français, sans erreur.
//
// Usage dans un composant :
//   const tp = useProductTexts();                   // fonction : produit → produit avec titre / description_courte / etiquette traduits
//   <Typography>{tp(product).titre}</Typography>
//   const longue = useProductLongDescription(product);   // texte traduit ou null (alors, afficher le français)
//   const page = usePageTexts('contact');            // { titre, contenu, meta_title, meta_description } ou null
import { useEffect, useMemo, useState } from 'react';
import { useLang, DEFAULT_LANG } from '../i18n';

const cache = new Map(); // adresse du fichier -> promesse de son contenu (objet) ; {} si absent ou illisible

const charger = (adresse) => {
  if (!cache.has(adresse)) {
    cache.set(adresse, fetch(adresse, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok && /json/i.test(r.headers.get('content-type') || '') ? r.json() : {}))
      .catch(() => ({})));
  }
  return cache.get(adresse);
};

// Test : vider le cache entre deux essais.
export const _viderCacheProductI18n = () => cache.clear();

const useFichier = (adresse) => {
  const [contenu, setContenu] = useState(null);
  useEffect(() => {
    let vivant = true;
    setContenu(null);
    if (adresse) charger(adresse).then((c) => { if (vivant) setContenu(c); });
    return () => { vivant = false; };
  }, [adresse]);
  return contenu;
};

/** Renvoie `tp(produit)` : le produit avec titre / description_courte / etiquette traduits (repli : français). */
export const useProductTexts = () => {
  const { lang } = useLang();
  const index = useFichier(lang === DEFAULT_LANG ? null : `/i18n/produits/${lang}.json`);
  return useMemo(() => (produit) => {
    const tr = index && produit && index[produit.url];
    if (!tr) return produit;
    return {
      ...produit,
      titre: tr.titre || produit.titre,
      description_courte: tr.description_courte || produit.description_courte,
      etiquette: tr.etiquette || produit.etiquette,
    };
  }, [index]);
};

/** Description longue traduite d'un produit (markdown) ou null : dans ce cas, afficher la description française. */
export const useProductLongDescription = (produit) => {
  const { lang } = useLang();
  const url = produit && produit.url;
  const fichier = useFichier(lang !== DEFAULT_LANG && url ? `/i18n/produits/${lang}/${encodeURIComponent(url)}.json` : null);
  return (fichier && fichier.description) || null;
};

/** Textes traduits d'une page en base (Contact, Accueil…) ou null. */
export const usePageTexts = (url) => {
  const { lang } = useLang();
  const index = useFichier(lang === DEFAULT_LANG ? null : `/i18n/pages/${lang}.json`);
  return (index && url && index[url]) || null;
};
